/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Présentation structurée de l'analyse juridique IA (Mémorandum juridique professionnel) :
 *  - Bloc 1 : Synthèse de la situation
 *  - Bloc 2 : Qualification juridique du problème
 *  - Bloc 3 : Fondements juridiques identifiés
 *  - Bloc 4 : Analyse juridique détaillée (Règles, Application, Limites, Conclusion)
 *  - Bloc 5 : Démarches recommandées
 *  - Bloc 6 : Documents juridiques associés à consulter
 *  - Barre d'actions : Nouvelle analyse, Copier l'analyse, Consulter les sources
 */

import React, { useRef, useState } from 'react';
import { 
  Sparkles, FileText, CheckCircle2, AlertTriangle, Scale, Copy, Check, 
  RotateCcw, BookOpen, ArrowRight, ShieldCheck, ExternalLink, Calendar, Landmark, Lock
} from 'lucide-react';
import { AiCaseAnalysisResponse } from '../../Types/ai';
import { LegalReferenceCard } from './LegalReferenceCard';
import { usePermissions } from '../../Hooks/usePermissions';
import { useAppSelector } from '../../ReduxToolkit/Hooks';

interface LegalAnalysisResultProps {
  response: AiCaseAnalysisResponse;
  onNewAnalysis: () => void;
  onOpenDocument: (documentId?: number) => void;
}

export const LegalAnalysisResult: React.FC<LegalAnalysisResultProps> = ({
  response,
  onNewAnalysis,
  onOpenDocument,
}) => {
  const [copied, setCopied] = useState(false);
  const sourcesRef = useRef<HTMLDivElement>(null);

  const { hasPermission } = usePermissions();
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const isAdmin = currentUser?.role === 'ADMIN';
  const canExportAi = hasPermission('export_ai_analysis') || isAdmin;

  const handleCopyAnalysis = () => {
    const fullText = `DOCARN JURIDIQUE — ANALYSE JURIDIQUE ASSISTÉE PAR IA
Réf. Requête : ${response.requestId}

1. SYNTHÈSE DE LA SITUATION
${response.summary}

2. QUALIFICATIONS JURIDIQUES
${response.legalQualifications.join(', ')}

3. FONDEMENTS JURIDIQUES IDENTIFIÉS
${response.legalReferences
  .map(
    (r) =>
      `• ${r.textNature} ${r.referenceNumber} - ${r.articleNumber || ''} (${r.officialTitle})\n  Extrait : "${r.articleExcerpt || ''}"\n  Pertinence : ${r.relevanceExplanation}`
  )
  .join('\n\n')}

4. ANALYSE JURIDIQUE
Règles applicables :
${response.legalAnalysis.applicableRules}

Application au cas :
${response.legalAnalysis.caseApplication}

Limites et réserves :
${response.legalAnalysis.limitationsAndCaveats}

Conclusion :
${response.legalAnalysis.conclusion}

5. DÉMARCHES RECOMMANDÉES
${response.recommendedActions.map((a, i) => `${i + 1}. ${a}`).join('\n')}

---
Avertissement : ${response.disclaimer}`;

    navigator.clipboard.writeText(fullText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const scrollToSources = () => {
    sourcesRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="w-full max-w-4xl space-y-6 pt-2 animate-fade-in text-zinc-900">
      {/* Simulation / Dev Notice si applicable */}
      {response.isDevelopmentSimulation && (
        <div className="px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-center justify-between gap-3 shadow-2xs">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <span>
              <strong>Environnement de test local :</strong> L'orchestrateur backend n'est pas encore déployé. Réponse générée via l'adaptateur de prévisualisation avec des textes du corpus camerounais.
            </span>
          </div>
          <span className="font-mono text-[10px] bg-amber-100 text-amber-900 font-bold px-2 py-0.5 rounded shrink-0">
            DÉMO
          </span>
        </div>
      )}

      {/* Top Banner : Mémorandum juridique */}
      <div className="bg-gradient-to-r from-[#1C0132] via-[#2A064B] to-[#3D0F73] text-white p-6 sm:p-7 rounded-3xl shadow-lg border border-primary-750/30 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-xs text-amber-300 font-bold uppercase tracking-wider">
            <Sparkles className="w-4 h-4 fill-current" />
            <span>Consultation & Mémorandum Juridique</span>
          </div>
          <h2 className="text-lg sm:text-xl font-display font-extrabold tracking-tight">
            Analyse de votre situation
          </h2>
          <p className="text-xs text-zinc-300">
            Requête analysée • {response.legalReferences.length} disposition(s) officielle(s) identifiée(s)
          </p>
        </div>

        {/* Action pills top */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleCopyAnalysis}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold backdrop-blur-xs transition cursor-pointer border border-white/20"
            title="Copier le contenu complet de l'analyse"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copied ? 'Copié !' : 'Copier'}</span>
          </button>

          <button
            type="button"
            onClick={onNewAnalysis}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white text-zinc-900 hover:bg-zinc-100 text-xs font-bold transition shadow-xs cursor-pointer border-none"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Nouvelle analyse</span>
          </button>
        </div>
      </div>

      {/* ── BLOC 1 : SYNTHÈSE DE LA SITUATION ─────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-zinc-200/80 p-6 shadow-xs space-y-3">
        <h4 className="text-xs font-extrabold uppercase tracking-wider text-zinc-400 flex items-center gap-2">
          <FileText className="w-4 h-4 text-primary-750" />
          <span>Synthèse de la situation</span>
        </h4>
        <p className="text-sm sm:text-[15px] text-zinc-800 leading-relaxed font-medium">
          {response.summary}
        </p>
      </div>

      {/* ── BLOC 2 : QUALIFICATION JURIDIQUE DU PROBLÈME ──────────────────── */}
      {response.legalQualifications && response.legalQualifications.length > 0 && (
        <div className="bg-white rounded-2xl border border-zinc-200/80 p-6 shadow-xs space-y-3">
          <h4 className="text-xs font-extrabold uppercase tracking-wider text-zinc-400 flex items-center gap-2">
            <Scale className="w-4 h-4 text-primary-750" />
            <span>Qualification juridique du problème</span>
          </h4>
          <div className="flex flex-wrap gap-2 pt-0.5">
            {response.legalQualifications.map((qualification, idx) => (
              <span
                key={idx}
                className="px-3 py-1 bg-violet-50 text-violet-900 border border-violet-200/80 rounded-full text-xs font-bold tracking-tight shadow-2xs"
              >
                {qualification}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── BLOC 3 : TEXTES ET DISPOSITIONS APPLICABLES ──────────────────── */}
      <div className="bg-white rounded-2xl border border-zinc-200/80 p-6 shadow-xs space-y-4">
        <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
          <h4 className="text-xs font-extrabold uppercase tracking-wider text-zinc-400 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-primary-750" />
            <span>Fondements juridiques identifiés</span>
          </h4>
          <span className="text-xs font-bold text-zinc-500 font-mono">
            {response.legalReferences.length} texte(s)
          </span>
        </div>

        <div className="space-y-3.5">
          {response.legalReferences.map((ref, idx) => (
            <LegalReferenceCard
              key={idx}
              reference={ref}
              onOpenDocument={onOpenDocument}
            />
          ))}
        </div>
      </div>

      {/* ── BLOC 4 : ANALYSE JURIDIQUE DÉTAILLÉE ─────────────────────────── */}
      <div className="bg-white rounded-2xl border border-zinc-200/80 p-6 sm:p-7 shadow-xs space-y-5">
        <h4 className="text-xs font-extrabold uppercase tracking-wider text-zinc-400 flex items-center gap-2 border-b border-zinc-100 pb-3">
          <Scale className="w-4 h-4 text-primary-750" />
          <span>Analyse juridique argumentée</span>
        </h4>

        {/* 1. Règles applicables */}
        <div className="space-y-1.5">
          <h5 className="text-xs font-extrabold text-zinc-900 uppercase tracking-wide">
            1. Règles juridiques applicables
          </h5>
          <p className="text-xs sm:text-sm text-zinc-700 leading-relaxed">
            {response.legalAnalysis.applicableRules}
          </p>
        </div>

        {/* 2. Application au cas d'espèce */}
        <div className="space-y-1.5 pt-2 border-t border-zinc-100">
          <h5 className="text-xs font-extrabold text-zinc-900 uppercase tracking-wide">
            2. Application à la situation décrite
          </h5>
          <p className="text-xs sm:text-sm text-zinc-700 leading-relaxed">
            {response.legalAnalysis.caseApplication}
          </p>
        </div>

        {/* 3. Limites, exceptions et éléments de vigilance */}
        <div className="space-y-1.5 pt-2 border-t border-zinc-100">
          <h5 className="text-xs font-extrabold text-zinc-900 uppercase tracking-wide flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            <span>3. Limites, exceptions et points de vigilance</span>
          </h5>
          <p className="text-xs sm:text-sm text-zinc-700 leading-relaxed bg-amber-50/50 p-3.5 rounded-xl border border-amber-200/50">
            {response.legalAnalysis.limitationsAndCaveats}
          </p>
        </div>

        {/* 4. Conclusion juridique */}
        <div className="space-y-1.5 pt-2 border-t border-zinc-100">
          <h5 className="text-xs font-extrabold text-zinc-900 uppercase tracking-wide">
            4. Conclusion juridique motivée
          </h5>
          <div className="p-4 rounded-xl bg-violet-50/60 border border-violet-100 text-xs sm:text-sm text-violet-950 font-medium leading-relaxed">
            {response.legalAnalysis.conclusion}
          </div>
        </div>
      </div>

      {/* ── BLOC 5 : DÉMARCHES RECOMMANDÉES ─────────────────────────────── */}
      {response.recommendedActions && response.recommendedActions.length > 0 && (
        <div className="bg-white rounded-2xl border border-zinc-200/80 p-6 shadow-xs space-y-4">
          <h4 className="text-xs font-extrabold uppercase tracking-wider text-zinc-400 flex items-center gap-2 border-b border-zinc-100 pb-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span>Quelles démarches entreprendre ?</span>
          </h4>

          <ol className="space-y-2.5">
            {response.recommendedActions.map((action, idx) => (
              <li
                key={idx}
                className="flex items-start gap-3 p-3 rounded-xl bg-zinc-50 border border-zinc-200/60 text-xs sm:text-sm text-zinc-800 leading-relaxed"
              >
                <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 font-extrabold text-[11px] flex items-center justify-center shrink-0 mt-0.5">
                  {idx + 1}
                </span>
                <span>{action}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* ── BLOC 6 : DOCUMENTS JURIDIQUES ASSOCIÉS ──────────────────────── */}
      {response.relatedDocuments && response.relatedDocuments.length > 0 && (
        <div ref={sourcesRef} className="bg-white rounded-2xl border border-zinc-200/80 p-6 shadow-xs space-y-4">
          <h4 className="text-xs font-extrabold uppercase tracking-wider text-zinc-400 flex items-center gap-2 border-b border-zinc-100 pb-3">
            <BookOpen className="w-4 h-4 text-primary-750" />
            <span>Documents juridiques associés dans Docarn</span>
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {response.relatedDocuments.map((doc) => (
              <div
                key={doc.id}
                onClick={() => onOpenDocument(doc.id)}
                className="p-4 bg-zinc-50/60 hover:bg-violet-50/40 border border-zinc-200/80 hover:border-violet-200 rounded-xl transition-all cursor-pointer group flex flex-col justify-between gap-3 shadow-2xs"
              >
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] font-bold bg-primary-50 text-primary-750 border border-primary-200/50 px-2 py-0.5 rounded-full uppercase">
                      {doc.nature}
                    </span>
                    <span className="font-mono text-xs font-semibold text-zinc-600 truncate">
                      {doc.referenceNumber}
                    </span>
                  </div>

                  <h5 className="text-xs font-bold text-zinc-900 group-hover:text-primary-750 transition-colors line-clamp-2 leading-snug">
                    {doc.officialTitle}
                  </h5>
                </div>

                <div className="flex items-center justify-between text-[11px] text-zinc-500 pt-2 border-t border-zinc-100">
                  {doc.effectiveYear && <span>Année : {doc.effectiveYear}</span>}
                  <span className="font-bold text-primary-750 flex items-center gap-1 group-hover:underline">
                    <span>Consulter</span>
                    <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom Action Footer Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-zinc-100 border border-zinc-200 rounded-2xl text-xs">
        <p className="text-[11px] text-zinc-500 italic max-w-lg leading-relaxed">
          {response.disclaimer}
        </p>

        <div className="flex items-center gap-2 shrink-0">
          {canExportAi ? (
            <button
              type="button"
              onClick={handleCopyAnalysis}
              className="px-4 py-2 bg-white hover:bg-zinc-50 border border-zinc-200 rounded-xl font-bold text-zinc-800 transition cursor-pointer flex items-center gap-1.5 shadow-2xs"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 text-zinc-500" />}
              <span>{copied ? 'Analyse copiée' : 'Copier l\'analyse'}</span>
            </button>
          ) : (
            <button
              type="button"
              disabled
              title="Votre profil ne dispose pas du privilège de copie ou d'export des analyses IA"
              className="px-4 py-2 bg-zinc-100 border border-zinc-200 text-zinc-400 rounded-xl font-semibold text-xs flex items-center gap-1.5 cursor-not-allowed opacity-80"
            >
              <Lock className="w-3.5 h-3.5 text-zinc-400" />
              <span>Copie restreinte</span>
            </button>
          )}

          <button
            type="button"
            onClick={onNewAnalysis}
            className="px-4 py-2 bg-primary-750 hover:bg-primary-800 text-white rounded-xl font-bold transition shadow-xs cursor-pointer border-none flex items-center gap-1.5"
          >
            <Sparkles className="w-3.5 h-3.5 text-amber-300" />
            <span>Nouvelle question</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalAnalysisResult;
